// Thin fetch wrapper. Same-origin relative paths by default (works whether
// the frontend is served by the backend directly, or dev-proxied by Vite —
// see vite.config.ts); override with VITE_API_BASE if the backend is ever
// hosted on a different origin.

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") ?? "";

export class ApiError extends Error {
  status?: number;
  detail?: unknown;
  constructor(message: string, status?: number, detail?: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

// --- Auth (phase 2) -------------------------------------------------------
// The backend's real auth contract (read from its own /openapi.json once it
// landed, 2026-09-20) is a session COOKIE, not a bearer token — there is no
// token anywhere in LoginResponse/MeResponse. `credentials: "include"` is
// what actually matters here: it makes the browser send/accept that cookie
// even when VITE_API_BASE points at a different origin in dev (same-origin
// requests would carry it by default regardless). See state/AuthContext.tsx
// for the full contract this was built against.
const FETCH_CREDENTIALS: RequestCredentials = "include";

// Fired on any 401 from any request, so AuthContext can drop a stale/expired
// session in one place rather than every call site checking status itself.
// The WebSocket layer calls the same handler on an auth-flavored close code
// (see useWebSocket.ts) so an expired session degrades the same way whether
// it's discovered via a poll or via the live socket.
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { signal, credentials: FETCH_CREDENTIALS, headers: { Accept: "application/json" } });
  } catch (e) {
    throw new ApiError(`network error reaching ${path}: ${(e as Error).message}`);
  }
  if (res.status === 401) unauthorizedHandler?.();
  if (!res.ok) {
    throw new ApiError(`${path} responded ${res.status}`, res.status);
  }
  try {
    return (await res.json()) as T;
  } catch (e) {
    throw new ApiError(`${path} returned invalid JSON`);
  }
}

/**
 * Like apiGet, but a 404 (endpoint not implemented by this backend yet)
 * resolves to `null` instead of throwing. Several endpoints (observer
 * history, per-node history, regions) are recent backend additions this
 * degrades gracefully against on an older build — an honest "not available
 * yet" state instead of a thrown error, without masking a real failure
 * (5xx, network) which still throws.
 */
export async function tryApiGet<T>(path: string, signal?: AbortSignal): Promise<T | null> {
  try {
    return await apiGet<T>(path, signal);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/**
 * Fetches a path and returns its status + parsed JSON body (if any)
 * regardless of whether the response was ok — never throws, never fires
 * the unauthorized handler. Used where a non-2xx response's *body* is worth
 * reading (e.g. a 401 that also signals setup_required) rather than just
 * its status code, which apiGet already covers for the common case.
 */
export async function apiGetWithBody<T = unknown>(path: string, signal?: AbortSignal): Promise<{ status: number; body: T | null }> {
  try {
    const res = await fetch(`${API_BASE}${path}`, { signal, credentials: FETCH_CREDENTIALS, headers: { Accept: "application/json" } });
    let body: T | null = null;
    try {
      body = (await res.json()) as T;
    } catch {
      /* no/invalid JSON body — fine, status alone is still returned */
    }
    return { status: res.status, body };
  } catch {
    return { status: 0, body: null };
  }
}

export async function apiPost<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      signal,
      credentials: FETCH_CREDENTIALS,
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new ApiError(`network error reaching ${path}: ${(e as Error).message}`);
  }
  if (res.status === 401) unauthorizedHandler?.();
  if (!res.ok) {
    let detail = "";
    try {
      const j = await res.json();
      detail = j?.detail ? ` — ${j.detail}` : "";
    } catch {
      /* body wasn't JSON; fine, no extra detail */
    }
    throw new ApiError(`${path} responded ${res.status}${detail}`, res.status);
  }
  try {
    return (await res.json()) as T;
  } catch {
    return undefined as T;
  }
}

export async function apiPatch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "PATCH",
      signal,
      credentials: FETCH_CREDENTIALS,
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new ApiError(`network error reaching ${path}: ${(e as Error).message}`);
  }
  if (res.status === 401) unauthorizedHandler?.();
  if (!res.ok) {
    throw new ApiError(`${path} responded ${res.status}`, res.status);
  }
  try {
    return (await res.json()) as T;
  } catch {
    return undefined as T;
  }
}

// Saved audit views (GET/POST/PUT/DELETE /api/audit/views) is the first
// caller that needs a real PUT verb — every other write path in this app
// so far has been POST (create) or PATCH (partial update). Mirrors
// apiPost's own error-detail extraction (the 403/409 cases PUT
// /api/audit/views/{id} returns — builtin-protected, name conflict —
// carry a real `detail` message worth surfacing, same as apiPost's own
// justification for doing this and apiPatch NOT doing it).
export async function apiPut<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "PUT",
      signal,
      credentials: FETCH_CREDENTIALS,
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new ApiError(`network error reaching ${path}: ${(e as Error).message}`);
  }
  if (res.status === 401) unauthorizedHandler?.();
  if (!res.ok) {
    let detail = "";
    try {
      const j = await res.json();
      detail = j?.detail ? ` — ${j.detail}` : "";
    } catch {
      /* body wasn't JSON; fine, no extra detail */
    }
    throw new ApiError(`${path} responded ${res.status}${detail}`, res.status);
  }
  try {
    return (await res.json()) as T;
  } catch {
    return undefined as T;
  }
}

export async function apiDelete<T = void>(path: string, signal?: AbortSignal): Promise<T | undefined> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { method: "DELETE", signal, credentials: FETCH_CREDENTIALS, headers: { Accept: "application/json" } });
  } catch (e) {
    throw new ApiError(`network error reaching ${path}: ${(e as Error).message}`);
  }
  if (res.status === 401) unauthorizedHandler?.();
  if (!res.ok) {
    throw new ApiError(`${path} responded ${res.status}`, res.status);
  }
  if (res.status === 204) return undefined;
  try {
    return (await res.json()) as T;
  } catch {
    return undefined;
  }
}

export function wsUrl(path: string): string {
  const base = (import.meta.env.VITE_WS_BASE as string | undefined) ?? "";
  // No token to attach — the WS handshake is same-origin and carries the
  // session cookie automatically, same as any other fetch (see the auth
  // comment above).
  return base ? `${base}${path}` : `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}${path}`;
}

// Admin region manager (2026-10-04): every /api/regions/mesh write must show
// the API's own validation message VERBATIM (`detail`, as the server wrote
// it), not "path responded 422 — ...". `body` may be a FormData (file
// import) -- the browser then sets the multipart Content-Type itself.
export async function apiSendVerbatim<T>(method: "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: FETCH_CREDENTIALS,
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    });
  } catch (e) {
    throw new ApiError(`network error reaching ${path}: ${(e as Error).message}`);
  }
  if (res.status === 401) unauthorizedHandler?.();
  if (!res.ok) {
    let detail: unknown = null;
    try {
      const j = await res.json();
      detail = j?.detail ?? null;
    } catch {
      /* body wasn't JSON */
    }
    const text = typeof detail === "string" ? detail : detail === null ? "" : JSON.stringify(detail);
    throw new ApiError(text || `${path} responded ${res.status}`, res.status, detail);
  }
  try {
    return (await res.json()) as T;
  } catch {
    return undefined as T;
  }
}
